import {
  BaSeSmall1,
  BaSeSmall2,
  BaSeTheme,
  BaSeCard,
  useOutsideEvent,
  BaSeShapeButton,
  BaSeHeading5,
  BaSeDialogContext,
  BaSeIcon,
  BaSeBreakpointContext,
  BaSeLoadingSpinner,
} from '@base/react';
import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import { AppContext } from '../context/index';
import { usePopper } from 'react-popper';
import { useEmpresa } from '../../../services/empresa/EmpresaService';
import { SaldoBaseModal } from '../modal/saldo-base/saldo-base';
import { EmpresaInativaModal } from '../modal/empresa-inativa/empresa-inativa';
import {
  ListChangeCard,
  SelectCard,
  WrapperContentCard,
  WrapperContentInfo,
  WrapperNewForm,
  WrapperRenderContentInfo,
} from './pick-company-styled';
import { LoadingWrapper } from '../../../pages/selecionar-empresa/styles';

const MAX_CARACTER_DISPLAY = 20;
const LABEL_ALTERAR_EMPRESA = 'Alterar minha empresa';
const DEFAULT_CPE_URL = 'https://cpe-frontend.homologacao.sebrae.com.br/';
const URL_REFERRER =
  '/?referrer=fluxo-caixa-frontend&referrer_uri=https://fluxocaixa.sebrae.com.br/&referrer_name=Fluxo%20de%20Caixa';

const MaskedCnpj = ({ cnpj }) => {
  const maskedCnpj = Sebrae.useMask(cnpj, 'cnpj');
  return <>{maskedCnpj}</>;
};

const truncateText = (text, maxLength = MAX_CARACTER_DISPLAY) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

const getCompanyDisplayName = (empresa, maskedCnpj) => {
  return (
    empresa?.apelido ?? empresa?.nome ?? empresa?.razaoSocial ?? maskedCnpj
  );
};

const getCompanyColor = (cor, defaultColor = '#01449F') => {
  return cor ? `#${cor}` : defaultColor;
};

const CompanyCard = React.memo(({ empresa, isSelected, onSelect, colors }) => {
  const { cinzaSebrae45, azulSebrae36 } = colors;
  const { white } = BaSeTheme.colors.defaultColors;

  return (
    <div style={{ cursor: 'pointer' }} onClick={onSelect}>
      <BaSeCard
        destakColor={getCompanyColor(empresa?.cor)}
        elevationOnHoverOrFocus={false}
        disposition="stacked"
        width="100%"
        shadowLevel={2}
        borderColor={white}
        renderContentInfo={() => (
          <WrapperContentInfo>
            <div>
              <BaSeIcon
                size={26}
                color={getCompanyColor(empresa?.cor)}
                description={empresa?.cnpj ?? 'Nome da empresa'}
                name={empresa?.icone?.nome ?? 'building'}
              />
            </div>
            <WrapperContentCard>
              <BaSeSmall1 isBold color={cinzaSebrae45}>
                {empresa?.apelido ?? empresa?.nome}
              </BaSeSmall1>
              <BaSeSmall1 color={cinzaSebrae45}>
                <MaskedCnpj cnpj={empresa?.cnpj} />
              </BaSeSmall1>
              <BaSeSmall1 color={cinzaSebrae45}>
                {empresa?.razaoSocial}
              </BaSeSmall1>
            </WrapperContentCard>
            {isSelected && (
              <BaSeIcon
                description="Empresa selecionada"
                name="check"
                size={20}
                color={azulSebrae36}
              />
            )}
          </WrapperContentInfo>
        )}
      />
    </div>
  );
});

CompanyCard.displayName = 'CompanyCard';

const CompanyDropdown = React.memo(
  ({
    empresas,
    isLoading,
    selectedEmpresaId,
    onSelectEmpresa,
    onAddCompany,
    onClose,
    colors,
  }) => {
    const { cinzaSebrae45 } = colors;

    return (
      <>
        <WrapperRenderContentInfo>
          <BaSeHeading5 isBold color={cinzaSebrae45}>
            Alterar minha empresa
          </BaSeHeading5>
          <BaSeShapeButton
            sizeIcon="big"
            nameIcon="times"
            type="tertiary"
            onClick={onClose}
          />
        </WrapperRenderContentInfo>
        <SelectCard>
          <BaSeSmall1 color={cinzaSebrae45}>Selecione a empresa</BaSeSmall1>
          {isLoading ? (
            <LoadingWrapper>
              <BaSeLoadingSpinner
                color="default"
                description="Carregando…"
                diameter={40}
                type="tertiary"
              />
            </LoadingWrapper>
          ) : (
            empresas?.map((emp) => (
              <CompanyCard
                key={emp?.cnpj}
                empresa={emp}
                isSelected={emp?.id === selectedEmpresaId}
                onSelect={() => onSelectEmpresa(emp)}
                colors={colors}
              />
            ))
          )}
          <div>
            <WrapperNewForm>
              <BaSeShapeButton
                shape="square"
                nameIcon="plus"
                type="secondary"
                onClick={onAddCompany}
              />
              <BaSeSmall1 color={cinzaSebrae45}>
                Cadastrar nova empresa
              </BaSeSmall1>
            </WrapperNewForm>
          </div>
        </SelectCard>
      </>
    );
  },
);

CompanyDropdown.displayName = 'CompanyDropdown';

// Hook customizado para gerenciar lógica de empresas
const useCompanyPicker = () => {
  const { navigateTo } = Sebrae.useRouter();
  const { showModal, showNotification } = React.useContext(BaSeDialogContext);
  const { atualizaEmpresa } = React.useContext(AppContext);
  const { buscarEmpresasUsuario, verificarSaldoEmpresa } = useEmpresa();
  const { getEnv } = Sebrae.useEnv();

  const [cpeUrl, setCpeUrl] = React.useState('');
  const [empresas, setEmpresas] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const loadCpeUrl = async () => {
      try {
        const url = await getEnv('cpeUrl');
        setCpeUrl(url);
      } catch (error) {
        console.error('Erro ao carregar CPE URL:', error);
      }
    };
    loadCpeUrl();
  }, [getEnv]);

  React.useEffect(() => {
    if (!open) return;

    setIsLoading(true);
    buscarEmpresasUsuario()
      .then(setEmpresas)
      .catch((error) => console.error('Erro ao buscar empresas:', error))
      .finally(() => setIsLoading(false));
  }, [buscarEmpresasUsuario, open]);

  const goToAddCompany = React.useCallback(() => {
    const baseUrl = cpeUrl || DEFAULT_CPE_URL;
    window.location.href = `${baseUrl}${URL_REFERRER}`;
  }, [cpeUrl]);

  const handleVincularEmpresaInativa = React.useCallback(() => {
    goToAddCompany();
  }, [goToAddCompany]);

  const handleContinueEmpresaInativa = React.useCallback(
    (empresa) => {
      navigateTo('/lancamentos');
      atualizaEmpresa(empresa);
    },
    [atualizaEmpresa, navigateTo],
  );

  const handleSelectEmpresa = React.useCallback(
    async (empresaSelecionada) => {
      try {
        const hasSaldo = await verificarSaldoEmpresa(empresaSelecionada);
        const isInativa =
          empresaSelecionada.isBaixada && !empresaSelecionada.isVinculada;

        if (!hasSaldo && !empresaSelecionada?.isConvidado) {
          setOpen(false);
          showModal({
            title: 'Saldo em caixa',
            content: ({ closeModal }) => (
              <SaldoBaseModal
                closeModal={closeModal}
                empresa={empresaSelecionada}
                atualizaEmpresa={atualizaEmpresa}
              />
            ),
            actions: [],
          });
          return;
        }
        if (isInativa) {
          setOpen(false);
          showModal({
            title: 'Atenção',
            actions: [],
            size: 'medium',
            content: ({ closeModal }) => (
              <EmpresaInativaModal
                onContinue={() =>
                  handleContinueEmpresaInativa(empresaSelecionada)
                }
                onVincularEmpresa={handleVincularEmpresaInativa}
                closeModal={closeModal}
              />
            ),
          });
          return;
        }

        atualizaEmpresa(empresaSelecionada);
        setOpen(false);
        navigateTo('/lancamentos');
      } catch (error) {
        console.error('Erro ao selecionar empresa:', error);
        showNotification({
          message: 'Erro ao selecionar empresa',
          supportMessage: error.message,
          color: 'destructive',
        });
      }
    },
    [
      atualizaEmpresa,
      navigateTo,
      showModal,
      showNotification,
      verificarSaldoEmpresa,
      handleContinueEmpresaInativa,
      handleVincularEmpresaInativa,
      setOpen,
    ],
  );

  return {
    cpeUrl,
    empresas,
    isLoading,
    open,
    setOpen,
    goToAddCompany,
    handleSelectEmpresa,
  };
};

export const PickComponent = () => {
  const { navigateTo } = Sebrae.useRouter();
  const { onDesktop, onDesktopLarge } = React.useContext(BaSeBreakpointContext);
  const { empresa } = React.useContext(AppContext);

  const {
    empresas,
    isLoading,
    open,
    setOpen,
    goToAddCompany,
    handleSelectEmpresa,
  } = useCompanyPicker();

  const [referenceElement, setReferenceElement] = React.useState(null);
  const [popperElement, setPopperElement] = React.useState(null);
  const [arrowElement, setArrowElement] = React.useState(null);
  const elementForClose = React.useRef(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
  });

  useOutsideEvent(elementForClose, setOpen);

  const colors = React.useMemo(
    () => ({
      azulSebrae36: BaSeTheme.colors.institucionais.azulSebrae36,
      cinzaSebrae45: BaSeTheme.colors.institucionais.cinzaSebrae45,
      white: BaSeTheme.colors.defaultColors.white,
    }),
    [],
  );

  const maskedCnpj = Sebrae.useMask(empresa?.cnpj, 'cnpj');
  const isHomePage = React.useMemo(() => window.location.pathname === '/', []);
  const isDesktopView = onDesktop || onDesktopLarge;
  const displayName = React.useMemo(
    () => truncateText(getCompanyDisplayName(empresa, maskedCnpj)),
    [empresa, maskedCnpj],
  );

  const handleChangeCompanyMobile = React.useCallback(() => {
    localStorage.clear();
    navigateTo('/');
  }, [navigateTo]);

  const handleToggleDropdown = React.useCallback(() => {
    if (!open) setOpen(true);
  }, [open, setOpen]);

  if (!empresa || isHomePage) {
    return null;
  }

  if (!isDesktopView) {
    return (
      <ListChangeCard>
        <BaSeSmall1 isBold color={colors.azulSebrae36}>
          {displayName}
        </BaSeSmall1>
        <div
          style={{ cursor: 'pointer' }}
          ref={setReferenceElement}
          onClick={handleChangeCompanyMobile}
        >
          <BaSeSmall2 link="" isBold color={colors.azulSebrae36}>
            {LABEL_ALTERAR_EMPRESA}
          </BaSeSmall2>
        </div>
      </ListChangeCard>
    );
  }

  return (
    <ListChangeCard>
      <BaSeSmall1 isBold color={colors.white}>
        {displayName}
      </BaSeSmall1>
      <div
        style={{ cursor: 'pointer' }}
        ref={setReferenceElement}
        onClick={handleToggleDropdown}
      >
        <BaSeSmall2 link="" isBold color={colors.white}>
          {LABEL_ALTERAR_EMPRESA}
        </BaSeSmall2>
      </div>
      {open && (
        <div
          ref={setPopperElement}
          style={{
            top: 50,
            zIndex: BaSeTheme.stackOrder.aboveOverlay,
            position: 'absolute',
          }}
          {...attributes.popper}
        >
          <div ref={elementForClose}>
            <BaSeCard
              elevationOnHoverOrFocus={false}
              withSideDestak={false}
              disposition="stacked"
              width="336px"
              shadowLevel={32}
              borderColor={colors.white}
              renderContentInfo={() => (
                <CompanyDropdown
                  empresas={empresas}
                  isLoading={isLoading}
                  selectedEmpresaId={empresa?.id}
                  onSelectEmpresa={handleSelectEmpresa}
                  onAddCompany={goToAddCompany}
                  onClose={() => setOpen(false)}
                  colors={colors}
                />
              )}
            />
            <div ref={setArrowElement} style={styles.arrow} />
          </div>
        </div>
      )}
    </ListChangeCard>
  );
};
